/**
 * Latin Square Generation
 *
 * Generates a random valid Latin square where each row and column
 * contains each number exactly once.
 */

/**
 * Shuffle an array in place using Fisher-Yates algorithm
 */
export function shuffle<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i]!;
    array[i] = array[j]!;
    array[j] = temp;
  }
  return array;
}

/**
 * Generate a random Latin square of given order
 *
 * Uses a simple backtracking algorithm that's efficient for small grids (≤9)
 */
export function generateLatinSquare(order: number): number[] {
  const grid = new Array<number>(order * order).fill(0);

  // Try to fill the grid
  if (fillLatinSquare(grid, order, 0)) {
    return grid;
  }

  // Fallback: use simple row-shift method if backtracking fails
  return generateSimpleLatinSquare(order);
}

/**
 * Recursive backtracking to fill Latin square
 */
function fillLatinSquare(grid: number[], order: number, pos: number): boolean {
  if (pos >= order * order) {
    return true; // Successfully filled
  }

  const x = pos % order;
  const y = Math.floor(pos / order);

  // Get available values for this cell
  const available = getAvailableValues(grid, order, x, y);
  shuffle(available);

  for (const value of available) {
    grid[pos] = value;

    if (fillLatinSquare(grid, order, pos + 1)) {
      return true;
    }

    grid[pos] = 0;
  }

  return false;
}

/**
 * Get values that don't conflict with row/column
 */
function getAvailableValues(
  grid: number[],
  order: number,
  x: number,
  y: number
): number[] {
  const used = new Set<number>();

  // Check row
  for (let xx = 0; xx < order; xx++) {
    const val = grid[y * order + xx] ?? 0;
    if (val !== 0) used.add(val);
  }

  // Check column
  for (let yy = 0; yy < order; yy++) {
    const val = grid[yy * order + x] ?? 0;
    if (val !== 0) used.add(val);
  }

  // Return unused values
  const available: number[] = [];
  for (let v = 1; v <= order; v++) {
    if (!used.has(v)) {
      available.push(v);
    }
  }

  return available;
}

/**
 * Simple Latin square using row shifts (fallback method)
 */
function generateSimpleLatinSquare(order: number): number[] {
  const grid = new Array<number>(order * order).fill(0);

  // Create first row: random permutation of 1..order
  const firstRow = Array.from({ length: order }, (_, i) => i + 1);
  shuffle(firstRow);

  // Create column shifts: random permutation of 0..order-1
  const shifts = Array.from({ length: order }, (_, i) => i);
  shuffle(shifts);

  // Fill grid using shifts
  for (let y = 0; y < order; y++) {
    const shift = shifts[y] ?? 0;
    for (let x = 0; x < order; x++) {
      const firstRowValue = firstRow[(x + shift) % order] ?? 1;
      grid[y * order + x] = firstRowValue;
    }
  }

  return grid;
}

/**
 * Validate that a grid is a valid Latin square
 */
export function validateLatinSquare(grid: number[], order: number): boolean {
  // Check each row
  for (let y = 0; y < order; y++) {
    const seen = new Set<number>();
    for (let x = 0; x < order; x++) {
      const val = grid[y * order + x] ?? 0;
      if (val < 1 || val > order || seen.has(val)) {
        return false;
      }
      seen.add(val);
    }
  }

  // Check each column
  for (let x = 0; x < order; x++) {
    const seen = new Set<number>();
    for (let y = 0; y < order; y++) {
      const val = grid[y * order + x] ?? 0;
      if (seen.has(val)) {
        return false;
      }
      seen.add(val);
    }
  }

  return true;
}
